import { useMemo, useState } from "react";
import {
  ConfiguratorStepper,
  ImageDisplay,
  ModalWrapper,
  StepWrapper,
  UserWarning,
} from "src/pages/configurator/components";
import {
  ComparePackageOptionsContent,
  ExpiredOptionsSwitch,
  OptionListContainer,
} from "src/pages/configurator/components/options";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { useComputed } from "src/utils/hooks/useComputed";
import { FlexibleOptionCard, getSelectedAduOptionCodes } from "~components";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { useTestIds } from "~utils";
import { getAduImages, getCutoffDaysRemaining, optionGroupFilter } from "../Configurator.utils";
import { InteriorStep_CheckoutOptionGroupFragment } from "./Interior.step.generated";

export function AduStep() {
  const { hasAduSupport, checkoutConfig, goToNextStep } = useConfiguratorContext();
  const tid = useTestIds({}, "adu-step");

  // If the plan doesn't have ADU support and we somehow ended up here, just go to the next step
  if (!hasAduSupport) {
    goToNextStep();
    return null;
  }

  // Legacy ADU uses option-group-based configuration
  if (checkoutConfig?.buildableAduPlans?.isEmpty) {
    return <LegacyAduStep />;
  }

  return (
    <div {...tid}>
      <StepWrapper
        hideBorder
        loading={false}
        loadingView="personalization"
        title="Add an ADU"
        description="Configure your ADU from available plans."
        left={<div>TODO: ADU plan selection</div>}
        right={<div>TODO: ADU configuration options</div>}
      />
      <ConfiguratorStepper />
    </div>
  );
}

function LegacyAduStep() {
  const { formState, checkoutConfig, selectedOptionIds, toggleOption, communityId } = useConfiguratorContext();
  const tid = useTestIds({}, "adu-step");
  const [compareCCOG, setCompareCCOG] = useState<InteriorStep_CheckoutOptionGroupFragment | undefined>(undefined);
  const [showExpiredOptions, setShowExpiredOptions] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const loading = useComputed(() => formState.loading, [formState]);
  const isDisabled = !checkoutConfig?.canEdit.allowed;

  const aduImages = getAduImages(checkoutConfig);

  // Pull OGs and check for expired
  const { aduOptionGroups, someOptionsHaveExpired } = useMemo(() => {
    const allAduOptionGroups =
      checkoutConfig?.optionGroups.filter((ccgo) => ccgo.step === CheckoutConfigOptionGroupStep.Adu) ?? [];

    const someOptionsHaveExpired = allAduOptionGroups.some(
      (ccog) => ccog.cutoffDate && getCutoffDaysRemaining(ccog.cutoffDate) <= 0,
    );

    // Only show option groups that do not have a parent (child groups are rendered inside their parent's card)
    const topLevelAduOptionGroups = allAduOptionGroups.filter((ccog) => !ccog.parentGroup);

    // Our option groups are already sorted and without any dedicated type sub heading to display we can just filter out the add adu group and show the rest
    const aduOptionGroups = topLevelAduOptionGroups.filter(optionGroupFilter(showExpiredOptions, selectedOptionIds));

    return {
      aduOptionGroups,
      someOptionsHaveExpired,
    };
  }, [checkoutConfig?.optionGroups, selectedOptionIds, showExpiredOptions]);

  // passed into floor plan component to highlight selected options
  const selectedAduOptionCodes = useMemo(
    () => getSelectedAduOptionCodes(aduOptionGroups, selectedOptionIds),
    [aduOptionGroups, selectedOptionIds],
  );

  if (checkoutConfig && !aduOptionGroups.some((ccog) => ccog.options.some((cco) => cco.option.code === "ADDADU"))) {
    // This is pretty bad. We don't want to block users from continuing but this indicates
    // a problem with our POF option setup since ADUs are always optional. So we want it fixed
    // ASAP.
    console.error(
      `Community: ${communityId}, Offering: ${checkoutConfig?.productOffering?.name}, CCFG ID:${checkoutConfig?.id}: No add ADU option group found while ADU specific options exist.`,
    );
  }
  return (
    <div data-testid="legacyAduStep">
      <StepWrapper
        hideBorder
        loading={loading}
        loadingView="personalization"
        title="Add an ADU"
        description="Add an ADU to your property for extra space or rental potential."
        warningNode={
          !loading &&
          isDisabled && (
            <UserWarning title="ADU options can no longer be changed." disabledReasons={[checkoutConfig?.canEdit]} />
          )
        }
        left={
          <ImageDisplay
            images={aduImages}
            selectedOptionCodes={selectedAduOptionCodes}
            imageIndex={selectedImageIndex}
            onClick={setSelectedImageIndex}
            {...tid.imageDisplay}
          />
        }
        right={
          <>
            {someOptionsHaveExpired && (
              <ExpiredOptionsSwitch
                showExpiredOptions={showExpiredOptions}
                setShowExpiredOptions={setShowExpiredOptions}
              />
            )}
            <OptionListContainer>
              <>
                {/* Note: these will render a placeholder image if you're working locally.
                  That doesn't match the designs but isn't an issue in prod because these are the same cards we've been usuing in the structural step where we also don't show an image
                */}
                {aduOptionGroups.nonEmpty && (
                  <>
                    {aduOptionGroups.map((ccog) => {
                      return (
                        <FlexibleOptionCard
                          key={ccog.group.id}
                          ccfgOptionGroup={ccog}
                          onCompare={(maybeChildCCOG) => setCompareCCOG(maybeChildCCOG ?? ccog)}
                          {...tid.flexibleOptionCard}
                        />
                      );
                    })}
                  </>
                )}
              </>
            </OptionListContainer>
          </>
        }
      />
      <ConfiguratorStepper />
      <ModalWrapper isOpen={!!compareCCOG} title={compareCCOG?.group.name} close={() => setCompareCCOG(undefined)}>
        {compareCCOG && checkoutConfig && (
          <ComparePackageOptionsContent
            checkoutConfigId={checkoutConfig.id}
            rpogId={compareCCOG.group.id}
            ccog={compareCCOG}
            selectedOptionIds={selectedOptionIds}
            onSelect={(selectedOptionId) =>
              toggleOption(
                compareCCOG,
                compareCCOG.options.find((cco) => cco.id === selectedOptionId) ?? fail(`Missing ${selectedOptionId}`),
              )
            }
          />
        )}
      </ModalWrapper>
    </div>
  );
}
